/**
 * Payer Mappings — the staff console's review board for saved insurance rules.
 *
 * Every rule at every active clinic of every practice, on one screen, without
 * viewing any practice as its team. The rules the system wrote by itself
 * (success learning, the AI suggestion pass) start unreviewed and stand out;
 * an operator reads a row — the insurance name exactly as the practice's
 * system sent it, the insurer it maps to, which part of the system wrote it,
 * what the check behind it answered, how the checks it steered have gone —
 * and approves it, rejects it, corrects its insurer, or deletes it. The
 * screen states facts and never passes judgement: "Eligible, no benefits
 * returned" is shown because it is true, not because it means the rule is
 * wrong.
 *
 * Every action carries the row's `expected` (the rule as this board showed
 * it). A `failed-precondition` refusal means the rule changed since the list
 * was loaded — another operator decided, the practice set it by hand — so
 * the board says so and reloads, and the operator judges the row again.
 * Reject and Delete ask first, naming what changes for the practice; Approve
 * is one click, because it changes nothing about how checks run.
 */
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { listClinicsForStaff, type StaffClinic, type StaffOrg } from '../services/staff'
import {
  listPayerAliasesForStaff,
  reviewPayerAliasForStaff,
  setPayerAliasForStaff,
  deletePayerAliasForStaff,
  viewPayerAliasCheckForStaff,
  searchPayersForStaff,
  expectedOf,
  type PayerAliasBoardRow,
  type PayerAliasCheckView,
} from '../services/payerAliases'
import PayerSearch, { type SelectedPayer } from './PayerSearch'
import BenefitsExplorer from './BenefitsExplorer'
import MedicalSlideout from './MedicalSlideout'
import { aliasHealthLabel } from '../utils/aliasHealthLabel'
import {
  GROUP_LABEL, GROUP_ORDER, groupOf, orderRows, writerLabel, answerLabel, decidedLabel,
  type RuleGroup,
} from '../utils/payerAliasBoard'
import { rpaVendorByTpId } from '../../shared/rpaRegistry'
import type { PayerAliasReview } from '../../shared/payerAlias'
import type { ConfirmedMember, EligibilityCheckDoc, Provider } from '../types'
import { normalizeStoredCheck } from '../services/eligibilityChecks'
// The staff console's visual language (KPIs / chips / table).
import './DataSyncRunHistory.css'
import './PayerAliasBoard.css'

interface PayerAliasBoardProps {
  /** Practice list, already loaded by StaffConsole (null while loading). */
  orgs: StaffOrg[] | null
  loadError: string | null
}

/** The refusal every stale action gets: the rule changed since the board
 *  loaded it. Everything else is an ordinary error, reported like every
 *  other outcome in the console's toast stack. */
function isRuleChanged(err: unknown): boolean {
  return (err as { code?: string })?.code === 'functions/failed-precondition'
}
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.'
}

const rowKey = (r: Pick<PayerAliasBoardRow, 'orgId' | 'clinicId' | 'key'>) => `${r.orgId}/${r.clinicId}/${r.key}`

/** The check behind a rule as the tenant app would render it: the patient
 *  block is the subscriber AS SENT, relationship self — exactly as the
 *  Dashboard builds it when it opens a stored check — the provider as the
 *  check recorded it, and the doc normalized by the read layer's own
 *  `normalizeStoredCheck` (V2 keys promoted, flow key, a pre-PaymentOption
 *  collection result relocated to the legacy card), so the slideout shows
 *  what the Cockpit and Recent Checks show for the same check. */
function checkViewSubject(check: PayerAliasCheckView): { member: ConfirmedMember; provider: Provider; doc: EligibilityCheckDoc } {
  const sub = check.subscriber
  const doc = normalizeStoredCheck({
    id: check.checkId,
    clinicId: check.clinicId ?? undefined,
    status: check.status ?? undefined,
    outcome: check.outcome ?? undefined,
    checkedAt: check.checkedAt ?? undefined,
    result: check.result,
    collectionResult: check.collectionResult ?? undefined,
    collectionResultV2: check.collectionResultV2 ?? undefined,
    correction: check.correction ?? undefined,
    userPick: check.userPick ?? undefined,
    flowId: check.flowId ?? undefined,
    eligibilityCheckSetId: check.eligibilityCheckSetId ?? undefined,
    stediPayerId: check.stediPayerId,
  } as unknown as EligibilityCheckDoc)
  return {
    doc,
    member: {
      firstName: sub?.firstName ?? '',
      lastName: sub?.lastName ?? '',
      memberId: sub?.memberId ?? '',
      dateOfBirth: sub?.dateOfBirth ?? '',
      relationship: 'self',
      isDependent: false,
    },
    provider: {
      id: 'learned-check-provider', clinicIds: [], defaultForClinicIds: [],
      firstName: check.provider?.name ?? 'Provider not known', lastName: '',
      npi: check.provider?.npi ?? '', phone: '', email: '',
    },
  }
}

type Confirm =
  | { kind: 'reject'; row: PayerAliasBoardRow }
  | { kind: 'delete'; row: PayerAliasBoardRow }

interface AddDraft {
  orgId: string
  clinicId: string
  clinics: StaffClinic[] | null
  shorthand: string
  payer: SelectedPayer | null
  attempted: boolean
  error: string | null
}

interface CheckView {
  row: PayerAliasBoardRow
  check: PayerAliasCheckView
  /** The check slideout over the board first; the explorer behind its
   *  View Full Benefits, whose back returns to the slideout. */
  mode: 'slideout' | 'explorer'
}

export default function PayerAliasBoard({ orgs, loadError }: PayerAliasBoardProps) {
  const [rows, setRows] = useState<PayerAliasBoardRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [groupFilter, setGroupFilter] = useState<'all' | RuleGroup>('all')
  const [practiceFilter, setPracticeFilter] = useState('all')
  const [search, setSearch] = useState('')

  const [busy, setBusy] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const [editing, setEditing] = useState<{ key: string; payer: SelectedPayer | null; attempted: boolean } | null>(null)
  const [add, setAdd] = useState<AddDraft | null>(null)
  const [checkView, setCheckView] = useState<CheckView | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await listPayerAliasesForStaff())
    } catch (err) {
      // An error state, never an empty list or zero counts: a board that
      // cannot load must not read as "nothing to review".
      Sentry.captureException(err, { tags: { action: 'listPayerAliasesForStaff' } })
      setRows(null)
      setError(`Couldn't load the rules. ${errorMessage(err)}`)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // ── Filters and counts ──────────────────────────────────────────────
  const practices = useMemo(() => {
    const m = new Map<string, string>()
    ;(rows ?? []).forEach((r) => m.set(r.orgId, r.orgName))
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [rows])

  // Scoped by practice (drives the chips' counts); the chip and the search
  // narrow the table.
  const scoped = useMemo(
    () => (rows ?? []).filter((r) => practiceFilter === 'all' || r.orgId === practiceFilter),
    [rows, practiceFilter],
  )
  const counts = useMemo(() => {
    const c: Record<RuleGroup, number> = { unreviewed: 0, rejected: 0, approved: 0, staff: 0 }
    scoped.forEach((r) => { c[groupOf(r)]++ })
    return c
  }, [scoped])
  const listed = useMemo(() => {
    const q = search.trim().toLowerCase()
    return orderRows(scoped.filter((r) =>
      (groupFilter === 'all' || groupOf(r) === groupFilter) &&
      (!q || r.raw.toLowerCase().includes(q) || r.resolvedName.toLowerCase().includes(q)),
    ))
  }, [scoped, groupFilter, search])

  // The Add rule dialog's insurer fields wake up together once a practice and
  // an active clinic are chosen — the directory is ranked by the clinic's usage.
  const addScoped = add !== null && add.orgId !== '' && add.clinicId !== ''

  // ── Actions ─────────────────────────────────────────────────────────
  /** Run one action against one row; a refusal reloads the board. Every
   *  outcome — the done line, a refusal, an error — goes to the console's
   *  toast stack (bottom right), the way the other staff sections report
   *  theirs; the row itself changes only through a reload. */
  async function act(row: PayerAliasBoardRow, fn: () => Promise<void>, done?: string) {
    const k = rowKey(row)
    setBusy(k)
    try {
      await fn()
      if (done) toast.success(done)
      await load()
    } catch (err) {
      if (isRuleChanged(err)) {
        // Another operator decided first, or the practice set the rule
        // meanwhile: nothing was saved. Show the row as it now is.
        toast.warning(`${errorMessage(err)} — showing it as it is now.`)
        await load()
      } else {
        Sentry.captureException(err, { tags: { action: 'payerAliasBoard' } })
        toast.error(errorMessage(err))
      }
    } finally {
      setBusy((b) => (b === k ? null : b))
    }
  }

  function review(row: PayerAliasBoardRow, decision: PayerAliasReview) {
    return act(row, () => reviewPayerAliasForStaff({
      orgId: row.orgId, clinicId: row.clinicId, key: row.key, decision, expected: expectedOf(row),
    }), decision === 'approved' ? `Approved "${row.raw}".` : `Rejected "${row.raw}".`)
  }

  function remove(row: PayerAliasBoardRow) {
    return act(row, () => deletePayerAliasForStaff({
      orgId: row.orgId, clinicId: row.clinicId, key: row.key, expected: expectedOf(row),
    }), `Deleted the rule for "${row.raw}".`)
  }

  async function saveEdit(row: PayerAliasBoardRow) {
    if (!editing || editing.key !== rowKey(row)) return
    if (!editing.payer) {
      setEditing({ ...editing, attempted: true })
      return
    }
    const payer = editing.payer
    await act(row, async () => {
      await setPayerAliasForStaff({
        orgId: row.orgId,
        clinicId: row.clinicId,
        shorthand: row.raw,
        route: rpaVendorByTpId(payer.tradingPartnerServiceId) ? 'rpa' : 'stedi',
        tradingPartnerServiceId: payer.tradingPartnerServiceId,
        stediPayerId: payer.stediId,
        resolvedName: payer.name,
        expected: expectedOf(row),
      })
      setEditing(null)
    }, `"${row.raw}" now maps to ${payer.name}.`)
  }

  async function openAdd() {
    setAdd({ orgId: '', clinicId: '', clinics: null, shorthand: '', payer: null, attempted: false, error: null })
  }

  async function chooseAddPractice(orgId: string) {
    setAdd((a) => a && { ...a, orgId, clinicId: '', clinics: null, payer: null, error: null })
    if (!orgId) return
    try {
      const clinics = (await listClinicsForStaff(orgId)).filter((c) => c.status === 'active')
      setAdd((a) => a && a.orgId === orgId ? { ...a, clinics } : a)
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'listClinicsForStaff' } })
      setAdd((a) => a && { ...a, clinics: [], error: `Couldn't load clinics. ${errorMessage(err)}` })
    }
  }

  async function saveAdd() {
    if (!add) return
    const ready = add.orgId && add.clinicId && add.shorthand.trim() && add.payer
    if (!ready) {
      setAdd({ ...add, attempted: true })
      return
    }
    const payer = add.payer as SelectedPayer
    setBusy('add')
    try {
      await setPayerAliasForStaff({
        orgId: add.orgId,
        clinicId: add.clinicId,
        shorthand: add.shorthand.trim(),
        route: rpaVendorByTpId(payer.tradingPartnerServiceId) ? 'rpa' : 'stedi',
        tradingPartnerServiceId: payer.tradingPartnerServiceId,
        stediPayerId: payer.stediId,
        resolvedName: payer.name,
        expected: { exists: false },
      })
      setAdd(null)
      toast.success(`Added the rule for "${add.shorthand.trim()}".`)
      await load()
    } catch (err) {
      if (isRuleChanged(err)) {
        setAdd(null)
        toast.warning(`${errorMessage(err)} — showing it as it is now.`)
        await load()
      } else {
        // The dialog stays open with what was typed, so the save can be retried.
        Sentry.captureException(err, { tags: { action: 'setPayerAliasForStaff' } })
        toast.error(errorMessage(err))
      }
    } finally {
      setBusy((b) => (b === 'add' ? null : b))
    }
  }

  async function openCheck(row: PayerAliasBoardRow) {
    const k = rowKey(row)
    setBusy(k)
    try {
      const check = await viewPayerAliasCheckForStaff({ orgId: row.orgId, clinicId: row.clinicId, key: row.key })
      if (!check.result) {
        toast.error('This check cannot be shown — it holds no stored result.')
        return
      }
      setCheckView({ row, check, mode: 'slideout' })
    } catch (err) {
      // Reported in the toast stack; the row and its actions still work.
      toast.error(`This check cannot be shown. ${errorMessage(err)}`)
    } finally {
      setBusy((b) => (b === k ? null : b))
    }
  }

  // ── The check behind a rule, read-only ──────────────────────────────
  // The check opens in the practice's own check slideout over the board
  // (rendered at the end of the board below); its View Full Benefits swaps
  // the screen for the explorer, whose back returns to the slideout.
  const viewed = checkView ? checkViewSubject(checkView.check) : null
  if (checkView && viewed && checkView.mode === 'explorer') {
    const { check } = checkView
    return (
      <div className="pab pab--check" data-testid="pab-check-view">
        <div className="pab-check__banner">
          Read-only · the check behind the rule for <b>{checkView.row.raw}</b> at {checkView.row.clinicName}
          {check.payer.name && <> · insurer asked: <b>{check.payer.name}</b></>}
          {check.origin === 'found' && <> · found as the check that preceded this rule (same name, clinic and insurer), not recorded on it</>}
        </div>
        <BenefitsExplorer
          result={viewed.doc.result as NonNullable<PayerAliasCheckView['result']>}
          confirmedMember={viewed.member}
          selectedProvider={viewed.provider}
          onBack={() => setCheckView({ ...checkView, mode: 'slideout' })}
          backLabel="Back to Payer Mappings"
        />
      </div>
    )
  }

  // ── The board ───────────────────────────────────────────────────────
  return (
    <div className="dsh pab">
      <div className="dsh-head">
        <div>
          <h1>Payer Mappings</h1>
          <div className="dsh-subt">Internal ops · every saved insurance rule · all practices</div>
        </div>
        <div className="dsh-head-meta">
          {rows && <>{rows.length} rules · {practices.length} practices</>}
          <button className="dsh-refresh" onClick={() => { void load() }} disabled={loading}>↻ {loading ? 'Refreshing…' : 'Refresh'}</button>
          <button className="dsh-refresh pab-add-btn" onClick={() => { void openAdd() }} disabled={!orgs}>+ Add rule</button>
        </div>
      </div>


      {error ? (
        <p className="dsh-error" role="alert">{error}</p>
      ) : (
        <div className="dsh-kpis pab-kpis">
          <div className={`dsh-kpi${counts.unreviewed > 0 ? ' dsh-kpi--alert' : ''}`}>
            <div className="dsh-kpi__l">Unreviewed</div>
            <div className="dsh-kpi__n" data-testid="count-unreviewed">{rows ? counts.unreviewed : '—'}</div>
            <div className="dsh-kpi__d">written by the system, nobody has decided</div>
          </div>
          <div className="dsh-kpi">
            <div className="dsh-kpi__l">Rejected</div>
            <div className="dsh-kpi__n" data-testid="count-rejected">{rows ? counts.rejected : '—'}</div>
            <div className="dsh-kpi__d">checks stopped until someone picks the insurer</div>
          </div>
          <div className="dsh-kpi">
            <div className="dsh-kpi__l">Approved</div>
            <div className="dsh-kpi__n" data-testid="count-approved">{rows ? counts.approved : '—'}</div>
            <div className="dsh-kpi__d">still monitored by the daily outcome check</div>
          </div>
          <div className="dsh-kpi">
            <div className="dsh-kpi__l">Set by hand</div>
            <div className="dsh-kpi__n" data-testid="count-staff">{rows ? counts.staff : '—'}</div>
            <div className="dsh-kpi__d">a person's decision · never overwritten</div>
          </div>
        </div>
      )}

      {loadError && <p className="dsh-error">{loadError}</p>}

      {!error && (
        <div className="dsh-qf">
          <button className={`dsh-chip${groupFilter === 'all' ? ' dsh-chip--on' : ''}`} onClick={() => setGroupFilter('all')}>
            All <span className="dsh-c">{scoped.length}</span>
          </button>
          {GROUP_ORDER.map((g) => (
            <button
              key={g}
              data-testid={`chip-${g}`}
              className={`dsh-chip${groupFilter === g ? ' dsh-chip--on' : ''}${g === 'unreviewed' ? ' dsh-chip--alert' : ''}`}
              onClick={() => setGroupFilter(g)}
            >
              {GROUP_LABEL[g]} <span className="dsh-c">{counts[g]}</span>
            </button>
          ))}
          <div className="dsh-qf__right">
            <input
              className="dsh-ddl pab-search"
              type="search"
              placeholder="Search PMS or VitalRev payer name…"
              aria-label="Search PMS or VitalRev payer name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="dsh-ddl" aria-label="Practice" value={practiceFilter} onChange={(e) => setPracticeFilter(e.target.value)}>
              <option value="all">All practices</option>
              {practices.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
        </div>
      )}

      {loading && !rows && !error && <p className="dsh-loading">Loading rules…</p>}

      {/* The card clips its rounded corners; while a row is being edited the
          picker's result list must hang below the table instead. */}
      {rows && !error && (
        <div className={`dsh-card${editing ? ' pab-card--editing' : ''}`}>
          <table className="dsh-table pab-table">
            <thead>
              <tr>
                <th>Practice · clinic</th>
                <th>PMS Payer Name</th>
                <th>VitalRev Payer Name</th>
                <th>Written by</th>
                <th>State</th>
                <th>Decided</th>
                <th>Check answered</th>
                <th>How it&apos;s doing</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listed.length === 0 && <tr><td colSpan={9} className="dsh-empty">No rules match these filters.</td></tr>}
              {listed.map((r) => {
                const k = rowKey(r)
                const group = groupOf(r)
                const selfWritten = r.source !== 'staff'
                const health = selfWritten ? aliasHealthLabel(r.health ?? undefined) : null
                const isEditing = editing?.key === k
                const rowBusy = busy === k
                // The whole row opens the check behind it (the Run History idiom).
                // A click on a button, a field or the inline editor is that control's
                // own, and a hand-set rule has no check to open.
                const openFromRow = selfWritten && !rowBusy
                  ? (e: MouseEvent<HTMLTableRowElement>) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select, textarea, label, .pab-edit')) return
                    void openCheck(r)
                  }
                  : undefined
                return (
                  <tr
                    key={k}
                    data-testid="pab-row"
                    data-group={group}
                    className={`pab-row pab-row--${group}${selfWritten ? ' pab-row--opens' : ''}`}
                    title={selfWritten ? 'Open the check behind this rule' : undefined}
                    onClick={openFromRow}
                  >
                    <td className="dsh-practice">
                      {r.orgName}
                      <div className="dsh-sub">{r.clinicName}</div>
                    </td>
                    <td className="pab-raw">{r.raw}</td>
                    <td>
                      {isEditing ? (
                        <div className="pab-edit">
                          <PayerSearch
                            value={editing.payer?.name ?? ''}
                            clinicId={r.clinicId}
                            searchFn={(input) => searchPayersForStaff({ orgId: r.orgId, clinicId: r.clinicId, query: input?.query ?? '' })}
                            onSelect={(p) => setEditing((e) => e && { ...e, payer: p })}
                            onClear={() => setEditing((e) => e && { ...e, payer: null })}
                            placeholder="Search the payer directory…"
                            hideLabel
                          />
                          {editing.attempted && !editing.payer && (
                            <div className="form-field__error">Pick the insurer from the directory.</div>
                          )}
                          <div className="pab-edit__actions">
                            <button className="dsh-refresh" onClick={() => { void saveEdit(r) }} disabled={rowBusy}>Save</button>
                            <button className="dsh-refresh" onClick={() => setEditing(null)} disabled={rowBusy}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {r.resolvedName}
                          <div className="dsh-sub">{r.route === 'rpa' ? 'Vision' : 'Medical'} · {r.tradingPartnerServiceId}</div>
                        </>
                      )}
                    </td>
                    <td className="pab-writer">{writerLabel(r)}</td>
                    <td>
                      <span className={`pab-state pab-state--${group}`} data-testid="pab-state">{GROUP_LABEL[group]}</span>
                    </td>
                    <td className="dsh-ts pab-decided">{decidedLabel(r)}</td>
                    <td className="pab-answer">
                      {answerLabel(r)}
                    </td>
                    <td>
                      {health && health.detail ? (
                        <span className={`alias-health alias-health--${health.tone}`} title={health.detail}>
                          {health.badge && <strong className="alias-health__badge">{health.badge}</strong>}
                          <span className="alias-health__detail">{health.detail}</span>
                        </span>
                      ) : (
                        <span className="alias-health alias-health--none">—</span>
                      )}
                    </td>
                    <td className="pab-actions">
                      {selfWritten && group !== 'approved' && (
                        <button className="dsh-refresh pab-approve" onClick={() => { void review(r, 'approved') }} disabled={rowBusy}>Approve</button>
                      )}
                      {selfWritten && group !== 'rejected' && (
                        <button className="dsh-refresh pab-reject" onClick={() => setConfirm({ kind: 'reject', row: r })} disabled={rowBusy}>Reject</button>
                      )}
                      {!isEditing && (
                        <button className="dsh-refresh pab-edit-btn" onClick={() => setEditing({ key: k, payer: null, attempted: false })} disabled={rowBusy}>Edit</button>
                      )}
                      <button className="dsh-refresh pab-delete" onClick={() => setConfirm({ kind: 'delete', row: r })} disabled={rowBusy}>Delete</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <div className="doctor-modal-overlay" onClick={() => setConfirm(null)}>
          <div
            className="doctor-modal pab-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pab-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title" id="pab-confirm-title">
                {confirm.kind === 'reject' ? 'Reject this rule?' : 'Delete this rule?'}
              </h3>
              <button className="doctor-modal__close" onClick={() => setConfirm(null)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <p className="pab-confirm__text" data-testid="confirm-text">
                {confirm.kind === 'reject'
                  ? `Checks for "${confirm.row.raw}" at ${confirm.row.clinicName} will stop until someone picks the insurer.`
                  : `"${confirm.row.raw}" goes back to being matched automatically at ${confirm.row.clinicName}.`}
              </p>
              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={() => setConfirm(null)}>Cancel</button>
                  <button
                    className="add-doctor-form__delete"
                    data-testid="confirm-ok"
                    onClick={() => {
                      const c = confirm
                      setConfirm(null)
                      void (c.kind === 'reject' ? review(c.row, 'rejected') : remove(c.row))
                    }}
                  >
                    {confirm.kind === 'reject' ? 'Reject' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {add && (
        <div className="doctor-modal-overlay" onClick={() => setAdd(null)}>
          <div className="doctor-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title">Add rule</h3>
              <button className="doctor-modal__close" onClick={() => setAdd(null)} aria-label="Close">×</button>
            </div>
            <div className="add-doctor-form">
              <div className="add-doctor-form__fields">
                <div className="form-field form-field--full">
                  <label className="form-field__label" htmlFor="pab-add-practice">Practice *</label>
                  <select
                    id="pab-add-practice"
                    className={`form-field__input${add.attempted && !add.orgId ? ' is-invalid' : ''}`}
                    value={add.orgId}
                    onChange={(e) => { void chooseAddPractice(e.target.value) }}
                  >
                    <option value="">Choose a practice…</option>
                    {/* A practice with no active clinic cannot take a rule, so it is not
                        offered. Compared to 0, not tested for truth, so a row from an
                        older backend that lacks the count still lists. */}
                    {(orgs ?? []).filter((o) => o.activeClinicCount !== 0).map((o) => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
                  </select>
                </div>
                <div className="form-field form-field--full">
                  <label className="form-field__label" htmlFor="pab-add-clinic">Clinic *</label>
                  <select
                    id="pab-add-clinic"
                    className={`form-field__input${add.attempted && !add.clinicId ? ' is-invalid' : ''}`}
                    value={add.clinicId}
                    disabled={!add.orgId || add.clinics === null}
                    onChange={(e) => setAdd((a) => a && { ...a, clinicId: e.target.value, payer: null })}
                  >
                    <option value="">
                      {add.orgId && add.clinics === null
                        ? 'Loading clinics…'
                        : add.clinics?.length === 0 && !add.error
                          ? 'No active clinics at this practice'
                          : 'Choose an active clinic…'}
                    </option>
                    {(add.clinics ?? []).map((c) => <option key={c.clinicId} value={c.clinicId}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-field form-field--full">
                  <label className="form-field__label" htmlFor="pab-add-shorthand">PMS Payer Name *</label>
                  <input
                    id="pab-add-shorthand"
                    className={`form-field__input${add.attempted && !add.shorthand.trim() ? ' is-invalid' : ''}`}
                    value={add.shorthand}
                    disabled={!addScoped}
                    onChange={(e) => setAdd((a) => a && { ...a, shorthand: e.target.value })}
                    placeholder="Exactly as the practice's system sends it, e.g. OHP"
                  />
                </div>
                <div className="form-field form-field--full">
                  <label className="form-field__label" htmlFor="pab-add-payer">VitalRev Payer Name *</label>
                  <PayerSearch
                    inputId="pab-add-payer"
                    value={add.payer?.name ?? ''}
                    clinicId={add.clinicId || undefined}
                    disabled={!addScoped}
                    searchFn={(input) => searchPayersForStaff({ orgId: add.orgId, clinicId: add.clinicId, query: input?.query ?? '' })}
                    onSelect={(p) => setAdd((a) => a && { ...a, payer: p })}
                    onClear={() => setAdd((a) => a && { ...a, payer: null })}
                    placeholder={addScoped ? 'Search the payer directory…' : 'Choose the practice and clinic first'}
                    hideLabel
                  />
                  {add.attempted && !add.payer && (
                    <div className="form-field__error">Pick the insurer from the directory.</div>
                  )}
                </div>
              </div>
              {add.error && <p className="clinic-save-error" role="alert">{add.error}</p>}
              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={() => setAdd(null)}>Cancel</button>
                  <button className="check-btn add-doctor-form__submit" onClick={() => { void saveAdd() }} disabled={busy === 'add'}>
                    {busy === 'add' ? 'Saving…' : 'Add rule'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {checkView && viewed && checkView.mode === 'slideout' && (
        <MedicalSlideout
          readOnly
          result={viewed.doc.result as NonNullable<PayerAliasCheckView['result']>}
          confirmedMember={viewed.member}
          selectedProvider={viewed.provider}
          onClose={() => setCheckView(null)}
          onViewFullBenefits={() => setCheckView({ ...checkView, mode: 'explorer' })}
          orgId={checkView.row.orgId}
          clinicId={checkView.row.clinicId}
          providers={[]}
          // Read-only: nothing in it can re-run, so this never fires.
          onRerunResult={() => {}}
          collectionResult={viewed.doc.collectionResult ?? null}
          legacyCollectionResult={viewed.doc.legacyCollectionResult ?? null}
          checkId={checkView.check.checkId}
          flowId={viewed.doc.flowId ?? null}
          storedStediId={checkView.check.stediPayerId}
          correctionOverride={viewed.doc.correction ?? null}
          userPick={viewed.doc.userPick ?? null}
          // What the practice's system called the insurer — the Cockpit passes
          // the row's raw payer the same way, for the "Suggested by VitalRev AI" mark.
          originalPayerName={checkView.check.payer.requestPayerName ?? undefined}
          checkedAt={checkView.check.checkedAt ?? undefined}
          timezone={checkView.check.timezone ?? undefined}
        />
      )}
    </div>
  )
}
